import { createBundleSubscriptionMutation } from "@/api/bundle_subscription";
import { PublishingStateEnum } from "@/api/common-types";
import { IProgressStepState } from "@/components/setup/ProgressStep.component";
import TextSection, {
  TextSectionSizeEnum,
} from "@/components/shared/TextSection.component";
import { IBundleSubscriptionInput } from "@/generated/types";
import { getApiDetails, IApiState } from "@/models";
import { ApiRegionEnum, publishApi } from "@/models/api";
import {
  getCheckoutSessionsByApiForSessionId,
  ICheckoutSessionState,
} from "@/models/checkout-session";
import {
  mapApiRegionToAWSRegionEnum,
  obtainAwsAccountId,
} from "@/models/coupon";
import { createSubscriptionItemsFromIds } from "@/models/subscription-item";
import { GraphapiModule } from "@/store/modules/graphapi";
import { ProductNameEnum } from "@graphapi-io/products";
import { Component, Vue, Watch } from "vue-property-decorator";
import * as tsx from "vue-tsx-support";
import { BContainer, BRow, BCol } from "@/lib/typed-bootstrap";
import styles from "./ApiSetupPublishing.view.module.scss";

// Child components
import StepProgressBar from "@/components/setup/StepProgressBar.component";
import { OrgModule } from "@/store";
import { isNotNullable } from "@/utils";
import { persistanceService } from "@/utils/persistance-service";

const createBundleSubscriptionAndSubscriptionItems = async (
  input: IBundleSubscriptionInput,
) => {
  const { data } = await createBundleSubscriptionMutation(input);

  const paymentProviderSubscriptionItemIds =
    data?.createBundleSubscription?.paymentProviderSubscriptionItemIds;
  const id = data?.createBundleSubscription?.id;

  if (id && paymentProviderSubscriptionItemIds) {
    await createSubscriptionItemsFromIds(
      paymentProviderSubscriptionItemIds.filter(isNotNullable),
      id,
    );
  }
};

@Component({
  components: { StepProgressBar, TextSection },
})
export default class ApiSetupPublishing extends Vue {
  _tsx!: tsx.DeclareProps<{}> & tsx.DeclareOnEvents<{}>;

  apiData: IApiState = {} as IApiState;

  publishingProgressOrder = [
    PublishingStateEnum.PUBLISHING,
    PublishingStateEnum.PUBLISHING_PREPARE,
    PublishingStateEnum.PUBLISHING_SCHEMA,
    PublishingStateEnum.PUBLISHING_RESOLVERS,
    PublishingStateEnum.PUBLISHED,
  ];

  publishingStageStarted: PublishingStateEnum[] = [];

  get steps(): IProgressStepState[] {
    return [
      {
        label: "Prepare Stack",
        labelKey: "api_publish_steps_1",
        inProgress: this.isPublishingPrep,
        done: this.isDonePublishingPrep,
        failed: this.publishingPrepFailed,
      },
      {
        label: "Create Schema",
        labelKey: "api_publish_steps_2",
        inProgress: this.isPublishingSchema,
        done: this.isDonePublishingSchema,
        failed: this.publishingSchemaFailed,
      },
      {
        label: "Deploy Resolvers",
        labelKey: "api_publish_steps_3",
        inProgress: this.isPublishingResolvers,
        done: this.isDonePublishingResolvers,
        failed: this.publishingResolversFailed,
      },
    ];
  }

  get currentApiState(): PublishingStateEnum | undefined {
    return GraphapiModule.states[this.apiData?.id] as PublishingStateEnum;
  }

  get prevApiState(): PublishingStateEnum {
    return this.publishingStageStarted.length > 0
      ? this.publishingStageStarted[this.publishingStageStarted.length - 1]
      : PublishingStateEnum.INITIALIZED;
  }

  get publishingStep(): number {
    return this.currentApiState
      ? this.publishingProgressOrder.indexOf(this.currentApiState)
      : -1;
  }

  get publishingFailed(): boolean {
    return this.currentApiState === PublishingStateEnum.PUBLISHING_FAILED;
  }

  get isPublishingPrep(): boolean {
    return this.publishingStep >= 0 && this.publishingStep <= 1;
  }

  get isDonePublishingPrep(): boolean {
    return this.publishingStep > 1;
  }

  get publishingPrepFailed(): boolean {
    return this.publishingFailed && this.isPublishingPrep;
  }

  get isPublishingSchema(): boolean {
    return this.publishingStep === 2;
  }

  get isDonePublishingSchema(): boolean {
    return this.publishingStep > 2;
  }

  get publishingSchemaFailed(): boolean {
    return this.publishingFailed && this.isPublishingSchema;
  }

  get isPublishingResolvers(): boolean {
    return this.publishingStep === 3;
  }

  get isDonePublishingResolvers(): boolean {
    return this.publishingStep > 3;
  }

  get publishingResolversFailed(): boolean {
    return this.publishingFailed && this.isPublishingResolvers;
  }

  async obtainAwsAccountId() {
    if (this.apiData?.awsAccountId) {
      return this.apiData.awsAccountId;
    }
    if (!this.apiData?.id) return null;

    const validAwsAccountID = await obtainAwsAccountId(
      mapApiRegionToAWSRegionEnum(
        (this.apiData.region as ApiRegionEnum) ?? ApiRegionEnum.EuropeFrankfurt,
      ),
      this.apiData.id,
    );

    return validAwsAccountID;
  }

  @Watch("currentApiState")
  onStateChange() {
    if (this.currentApiState && !this.publishingFailed) {
      if (this.currentApiState !== this.prevApiState) {
        this.publishingStageStarted.push(this.currentApiState);
      }
    }
  }

  async created() {
    const apiId = this.$route.params.apiId;
    if (!apiId) {
      console.error("API ID missing from route params");
      return;
    }

    try {
      this.apiData = await getApiDetails({ id: apiId });
      GraphapiModule.initializeStateSubscription({
        apiId: this.apiData.id,
        state: this.apiData.state,
      });

      if (
        this.apiData.state &&
        this.publishingProgressOrder.includes(this.apiData.state)
      ) {
        this.publishingStageStarted.push(this.apiData.state);
      }

      if (!this.publishingProgressOrder.includes(this.apiData.state)) {
        const sessionIdQuery = this.$route.query?.session_id;
        if (sessionIdQuery && !Array.isArray(sessionIdQuery)) {
          const sessionId = sessionIdQuery;
          const checkoutSessions = await getCheckoutSessionsByApiForSessionId(
            this.apiData.id,
            sessionId,
          );
          const session = checkoutSessions?.[0] as ICheckoutSessionState;

          if (session) {
            await createBundleSubscriptionAndSubscriptionItems({
              paymentProviderId: "",
              paymentProviderCustomerId: "",
              lineItems: session.lineItems,
              products: session.products?.filter(isNotNullable) ?? [],
              apiId: this.apiData.id,
              orgId: this.apiData.orgId ?? OrgModule.id,
              paymentProviderCheckoutSessionId: session.sessionId,
            });

            if (session.metadata) {
              try {
                const metadataItems = JSON.parse(session.metadata);
                if (Array.isArray(metadataItems) && metadataItems.length > 0) {
                  await createBundleSubscriptionAndSubscriptionItems({
                    paymentProviderId: "",
                    paymentProviderCustomerId: "",
                    lineItems: session.metadata,
                    products: [],
                    apiId: this.apiData.id,
                    orgId: this.apiData.orgId ?? OrgModule.id,
                    paymentProviderCheckoutSessionId: "",
                  });
                }
              } catch (parseError) {
                console.error("Failed to parse session metadata:", parseError);
              }
            }

            if (!window.Cypress) {
              this.apiData.awsAccountId = await this.obtainAwsAccountId();
              if (!this.apiData.awsAccountId) {
                throw new Error(
                  this.$t("error_api_create_requires_awsaccount").toString(),
                );
              }
              await publishApi(
                this.apiData,
                this.apiData.state,
                (session.products?.filter(
                  isNotNullable,
                ) as ProductNameEnum[]) ?? [],
              );
            }
          } else {
            console.warn(
              "No matching checkout session found for session ID:",
              sessionId,
            );
          }
        } else {
          console.warn("Session ID missing or invalid in route query.");
        }
      }
    } catch (err) {
      console.error("Error during ApiSetupPublishing created hook:", err);
      persistanceService.removeApiData();
    }
  }

  onClose() {
    if (this.apiData?.id) {
      this.$router.push({
        name: "project-overview",
        params: { apiId: this.apiData.id },
      });
    } else {
      this.$router.push({
        name: "org-api-dashboard",
        params: { orgId: OrgModule.id },
      });
    }
  }

  render() {
    const apiNameElement = (
      <span class={styles.apiName}>{this.apiData?.name ?? "your API"}</span>
    );

    return (
      <BContainer class={["default__view-setup", styles.apiSetupPublishing]}>
        <h1>{this.$t("api_setup_publish_title").toString()}</h1>
        <p>{this.$t("api_setup_publish_subtitle", { name: apiNameElement })}</p>
        <BContainer class="api-setup__wizard">
          <BRow>
            <BCol></BCol>
            <BCol md="8" class="api-setup__data-types-col p-md-4">
              <BRow class="actions__section mt-5">
                <StepProgressBar steps={this.steps} showSpinner={true} />
              </BRow>
              <div class={styles.textSection}>
                <TextSection
                  title={this.$t("api_publishing_disclaimer_title").toString()}
                >
                  {this.$t("api_publishing_disclaimer_content").toString()}
                </TextSection>
                <TextSection
                  title={this.$t("api_publishing_next_steps_title").toString()}
                  size={TextSectionSizeEnum.m}
                >
                  <ul>
                    <li>
                      {this.$t("api_publishing_next_step_docs").toString()}
                      <a
                        href="https://graphapi.com/docs"
                        target="_blank"
                        rel="noopener noreferrer"
                        class={styles.link}
                      >
                        https://graphapi.com/docs
                      </a>
                    </li>
                    <li>
                      {this.$t(
                        "api_publishing_next_step_learn_graphql",
                      ).toString()}
                      <a
                        href="https://graphapi.com/learn/graphql"
                        target="_blank"
                        rel="noopener noreferrer"
                        class={styles.link}
                      >
                        https://graphapi.com/learn/graphql
                      </a>
                    </li>
                    <li>
                      {this.$t("api_publishing_next_step_invite").toString()}
                    </li>
                    <li>
                      {this.$t(
                        "api_publishing_next_step_learn_queries",
                      ).toString()}
                    </li>
                    <li>
                      {this.$t("api_publishing_next_step_manage").toString()}
                    </li>
                  </ul>
                </TextSection>
              </div>
              <BRow class="buttons__section">
                <button
                  class="action-button__pill--outline mx-auto"
                  onClick={(e) => {
                    e.preventDefault();
                    this.onClose();
                  }}
                >
                  {this.$t("global_cta_close").toString()}
                </button>
              </BRow>
            </BCol>
            <BCol></BCol>
          </BRow>
        </BContainer>
      </BContainer>
    );
  }
}
